/**
 * 프론트엔드 권한 UI와 백엔드 권한 매핑 상수
 * 
 * 이 파일은 프론트엔드에서 권한 관리 UI를 구현할 때 사용하는 권한 구조를 정의합니다.
 * 백엔드 권한 코드와 프론트엔드 UI 구조를 매핑합니다.
 */

export interface PermissionItem {
  id: string;
  label: string;
  code: string; // 백엔드 권한 코드
  description?: string;
}

export interface PermissionCategory {
  id: string;
  label: string;
  permissions: PermissionItem[];
}

/**
 * 프론트엔드 UI 권한 카테고리 구조
 */
export const PERMISSION_CATEGORIES: PermissionCategory[] = [
  {
    id: 'orders',
    label: '주문 관리',
    permissions: [
      {
        id: 'orders:read',
        label: '주문 조회',
        code: 'orders:read',
        description: '주문 목록, 상세 정보, 통계 조회',
      },
      {
        id: 'orders:write',
        label: '주문 수정',
        code: 'orders:write',
        description: '주문 생성, 상태 변경, 취소, 일괄 처리',
      },
      {
        id: 'orders:delete',
        label: '주문 삭제',
        code: 'orders:delete',
        description: '주문 삭제 (현재 사용되지 않음)',
      },
      {
        id: 'orders:fulfill',
        label: '주문 처리',
        code: 'orders:fulfill',
        description: '주문 이행 처리 (현재 사용되지 않음)',
      },
      {
        id: 'orders:inquiry',
        label: '주문 문의 처리',
        code: 'orders:inquiry',
        description: '주문 관련 문의 생성, 조회, 응답, 할당, 닫기',
      },
    ],
  },
  {
    id: 'users',
    label: '회원 관리',
    permissions: [
      {
        id: 'users:read',
        label: '회원 조회',
        code: 'users:read',
        description: '회원 목록, 상세 정보, 포인트 이력 조회',
      },
      {
        id: 'users:write',
        label: '회원 수정',
        code: 'users:write',
        description: '회원 정보 수정, 포인트 적립/차감',
      },
      {
        id: 'users:delete',
        label: '회원 삭제',
        code: 'users:delete',
        description: '회원 계정 삭제/비활성화',
      },
    ],
  },
  {
    id: 'products',
    label: '상품 관리',
    permissions: [
      {
        id: 'products:read',
        label: '상품 조회',
        code: 'products:read',
        description: '상품 목록, 상세 정보, 통계 조회',
      },
      {
        id: 'products:write',
        label: '상품 등록',
        code: 'products:write',
        description: '상품 생성 (write 권한으로 처리)',
      },
      {
        id: 'products:write-edit',
        label: '상품 수정',
        code: 'products:write',
        description: '상품 정보 수정, 일괄 수정 (등록과 동일 권한)',
      },
      {
        id: 'products:delete',
        label: '상품 삭제',
        code: 'products:delete',
        description: '상품 삭제',
      },
      {
        id: 'products:sync',
        label: '상품 동기화',
        code: 'products:sync',
        description: '외부 플랫폼(1688, Taobao 등) 상품 동기화',
      },
    ],
  },
  {
    id: 'categories',
    label: '카테고리 관리',
    permissions: [
      {
        id: 'categories:read',
        label: '카테고리 조회',
        code: 'categories:read',
        description: '카테고리 트리 조회, 카테고리 상세 조회',
      },
      {
        id: 'categories:write',
        label: '카테고리 수정',
        code: 'categories:write',
        description: '카테고리 정보 수정, 이미지 자동 설정',
      },
      {
        id: 'categories:delete',
        label: '카테고리 삭제',
        code: 'categories:delete',
        description: '카테고리 삭제 (현재 사용되지 않음)',
      },
    ],
  },
  {
    id: 'warehouses',
    label: '창고 관리',
    permissions: [
      {
        id: 'warehouses:read',
        label: '창고 조회',
        code: 'warehouses:read',
        description: '창고 목록 조회, 창고 상세 조회, 창고 통계',
      },
      {
        id: 'warehouses:write',
        label: '창고 수정',
        code: 'warehouses:write',
        description: '창고 생성, 정보 수정, 삭제',
      },
    ],
  },
  {
    id: 'racks',
    label: '랙 관리',
    permissions: [
      {
        id: 'racks:read',
        label: '랙 조회',
        code: 'racks:read',
        description: '랙 목록 조회, 랙 상세 조회, 바코드 조회, 랙 통계, 랙 대시보드, 바코드 생성',
      },
      {
        id: 'racks:write',
        label: '랙 수정',
        code: 'racks:write',
        description: '랙 생성, 일괄 생성, 정보 수정, 삭제',
      },
    ],
  },
  {
    id: 'packages',
    label: '패키지 관리',
    permissions: [
      {
        id: 'packages:read',
        label: '패키지 조회',
        code: 'packages:read',
        description: '패키지 목록 조회, 패키지 상세 조회, 패키지 통계, 패키지 스캔',
      },
      {
        id: 'packages:write',
        label: '패키지 수정',
        code: 'packages:write',
        description: '패키지 상태 변경, 정보 수정',
      },
    ],
  },
  {
    id: 'warehouse-transactions',
    label: '창고 거래 조회',
    permissions: [
      {
        id: 'warehouse-transactions:read',
        label: '창고 거래 조회',
        code: 'warehouse-transactions:read',
        description: '창고 거래 목록 조회, 거래 상세 조회, 거래 통계, 재고 조회',
      },
    ],
  },
  {
    id: 'activity-logs',
    label: '활동 로그',
    permissions: [
      {
        id: 'activity-logs:read',
        label: '활동 로그 조회',
        code: 'activity-logs:read',
        description: '모든 활동 로그 조회',
      },
    ],
  },
  {
    id: 'settlement',
    label: '정산 관리',
    permissions: [
      {
        id: 'settlement:read',
        label: '정산 조회',
        code: 'orders:read',
        description: '주문별 결제/정산 정보 조회 (orders:read 권한 사용)',
      },
      {
        id: 'settlement:write',
        label: '정산 처리',
        code: 'orders:write',
        description: '결제 확인, 환불 처리 등 (orders:write 권한 사용)',
      },
    ],
  },
  {
    id: 'help-center',
    label: '고객센터 관리',
    permissions: [
      {
        id: 'help-center:read',
        label: '고객센터 조회',
        code: 'help-center:read',
        description: 'FAQ 조회, FAQ 카테고리 조회, 가이드 조회, 도움말 조회, 푸터 정보 조회, 소셜 링크 조회, 결제 수단 조회',
      },
      {
        id: 'help-center:write',
        label: '고객센터 수정',
        code: 'help-center:write',
        description: 'FAQ 생성/수정, FAQ 카테고리 생성/수정, 가이드 생성/수정, 도움말 수정, 푸터 정보 수정, 소셜 링크 생성/수정, 결제 수단 생성/수정',
      },
      {
        id: 'help-center:delete',
        label: '고객센터 삭제',
        code: 'help-center:delete',
        description: 'FAQ 삭제, FAQ 카테고리 삭제, 가이드 삭제, 소셜 링크 삭제, 결제 수단 삭제',
      },
    ],
  },
  {
    id: 'banners',
    label: '배너 관리',
    permissions: [
      {
        id: 'banners:read',
        label: '배너 조회',
        code: 'banners:read',
        description: '배너 목록 조회, 배너 상세 조회',
      },
      {
        id: 'banners:write',
        label: '배너 수정',
        code: 'banners:write',
        description: '배너 생성, 정보 수정',
      },
      {
        id: 'banners:delete',
        label: '배너 삭제',
        code: 'banners:delete',
        description: '배너 삭제',
      },
    ],
  },
  {
    id: 'content',
    label: '콘텐츠 관리',
    permissions: [
      {
        id: 'content:write',
        label: '콘텐츠 수정',
        code: 'content:write',
        description: '소유자 찾기 게시물 생성/수정',
      },
      {
        id: 'content:delete',
        label: '콘텐츠 삭제',
        code: 'content:delete',
        description: '소유자 찾기 게시물 삭제',
      },
    ],
  },
  {
    id: 'general-inquiry',
    label: '일반 문의 관리',
    permissions: [
      {
        id: 'general:inquiry',
        label: '일반 문의 처리',
        code: 'general:inquiry',
        description: '일반 문의 생성, 조회, 응답 (소켓 통신 및 서비스 레이어에서 사용)',
      },
    ],
  },
  {
    id: 'system',
    label: '시스템 관리',
    permissions: [
      {
        id: 'admins:read',
        label: '관리자 조회',
        code: 'admins:read',
        description: '관리자 목록, 상세 정보 조회',
      },
      {
        id: 'admins:write',
        label: '관리자 수정/생성',
        code: 'admins:write',
        description: '관리자 생성, 정보 수정',
      },
      {
        id: 'admins:delete',
        label: '관리자 삭제',
        code: 'admins:delete',
        description: '관리자 계정 삭제/비활성화',
      },
      {
        id: 'permissions:manage',
        label: '권한 관리',
        code: 'admins:write',
        description: '관리자 권한 설정 (admins:write 권한 사용)',
      },
      {
        id: 'settings:read',
        label: '설정 조회',
        code: 'settings:read',
        description: '시스템 설정 조회',
      },
      {
        id: 'settings:write',
        label: '설정 수정',
        code: 'settings:write',
        description: '시스템 설정 수정',
      },
    ],
  },
];

/**
 * UI 권한 ID를 백엔드 권한 코드로 변환하는 맵
 */
export const UI_TO_BACKEND_PERMISSION_MAP: Record<string, string> = {
  // 상품 관리: 등록과 수정이 동일 권한
  'products:write': 'products:write',
  'products:write-edit': 'products:write',
  
  // 정산 관리: 주문 권한으로 매핑
  'settlement:read': 'orders:read',
  'settlement:write': 'orders:write',
  
  // 권한 관리: 관리자 수정 권한으로 매핑
  'permissions:manage': 'admins:write',
};

/**
 * UI에서 선택된 권한 ID 배열을 백엔드 권한 코드 배열로 변환
 * 
 * @param uiPermissionIds - UI에서 선택된 권한 ID 배열
 * @returns 백엔드 권한 코드 배열 (중복 제거됨)
 */
export function convertUIToBackendPermissions(uiPermissionIds: string[]): string[] {
  const backendPermissions = new Set<string>();
  
  uiPermissionIds.forEach(uiId => {
    const backendCode = UI_TO_BACKEND_PERMISSION_MAP[uiId] || uiId;
    backendPermissions.add(backendCode);
  });
  
  return Array.from(backendPermissions);
}

/**
 * 백엔드 권한 코드 배열을 UI 권한 ID 배열로 변환
 * 
 * @param backendPermissions - 백엔드 권한 코드 배열
 * @returns UI 권한 ID 배열
 */
/**
 * Convert backend permission codes to UI permission IDs
 * 
 * IMPORTANT: Settlement permissions share the same backend codes as orders permissions
 * (settlement:read uses orders:read, settlement:write uses orders:write)
 * 
 * When converting from backend to UI:
 * - We need to preserve which UI permission was originally selected
 * - If backend has orders:read, we check previousUIPermissions to see if settlement:read was selected
 * - This allows settlement and orders permissions to be independent
 * 
 * @param backendPermissions - Array of backend permission codes
 * @param previousUIPermissions - Optional array of previously selected UI permission IDs (to preserve settlement vs orders selection)
 * @returns Array of UI permission IDs
 */
export function convertBackendToUIPermissions(
  backendPermissions: string[],
  currentUIPermissions?: string[]
): string[] {
  if (!Array.isArray(backendPermissions) || backendPermissions.length === 0) {
    return [];
  }
  
  const backendSet = new Set(backendPermissions);
  const uiPermissions: string[] = [];
  const currentUISet = currentUIPermissions ? new Set(currentUIPermissions) : new Set();
  
  // Track which backend codes we've already mapped to a UI permission
  const mappedBackendCodes = new Set<string>();
  
  PERMISSION_CATEGORIES.forEach(category => {
    category.permissions.forEach(permission => {
      // Check if backend has this permission code
      if (backendSet.has(permission.code) && !mappedBackendCodes.has(permission.code)) {
        // Special handling for orders/settlement conflict
        if (permission.code === 'orders:read') {
          // Check if settlement:read is currently selected in UI
          if (currentUISet.has('settlement:read') && !currentUISet.has('orders:read')) {
            // User has settlement:read selected, show settlement:read
            if (permission.id === 'settlement:read') {
              uiPermissions.push(permission.id);
              mappedBackendCodes.add(permission.code);
            }
          } else {
            // User has orders:read selected, or neither is selected - default to orders:read
            if (permission.id === 'orders:read') {
              uiPermissions.push(permission.id);
              mappedBackendCodes.add(permission.code);
            }
          }
        } else if (permission.code === 'orders:write') {
          // Check if settlement:write is currently selected in UI
          if (currentUISet.has('settlement:write') && !currentUISet.has('orders:write')) {
            // User has settlement:write selected, show settlement:write
            if (permission.id === 'settlement:write') {
              uiPermissions.push(permission.id);
              mappedBackendCodes.add(permission.code);
            }
          } else {
            // User has orders:write selected, or neither is selected - default to orders:write
            if (permission.id === 'orders:write') {
              uiPermissions.push(permission.id);
              mappedBackendCodes.add(permission.code);
            }
          }
        } else {
          // For all other permissions, add normally
          uiPermissions.push(permission.id);
          mappedBackendCodes.add(permission.code);
        }
      }
    });
  });
  
  return uiPermissions;
}

/**
 * 모든 백엔드 권한 코드 목록 (중복 제거)
 */
export const ALL_BACKEND_PERMISSIONS: string[] = Array.from(
  new Set(
    PERMISSION_CATEGORIES.flatMap(category =>
      category.permissions.map(perm => perm.code)
    )
  )
);

/**
 * 권한 ID로 권한 정보 찾기
 */
export function findPermissionById(id: string): PermissionItem | undefined {
  for (const category of PERMISSION_CATEGORIES) {
    const permission = category.permissions.find(p => p.id === id);
    if (permission) return permission;
  }
  return undefined;
}

/**
 * 백엔드 권한 코드로 권한 정보 찾기
 */
export function findPermissionByCode(code: string): PermissionItem | undefined {
  for (const category of PERMISSION_CATEGORIES) {
    const permission = category.permissions.find(p => p.code === code);
    if (permission) return permission;
  }
  return undefined;
}

/**
 * 상품 관리에서 등록/수정 권한이 연동되어 있는지 확인
 */
export function isLinkedProductPermission(permissionId: string): boolean {
  return permissionId === 'products:write' || permissionId === 'products:write-edit';
}

/**
 * 상품 권한 처리: 등록/수정은 항상 함께 선택/해제되어야 함
 */
export function handleProductPermissionChange(
  permissionId: string,
  checked: boolean,
  currentPermissions: Set<string>
): void {
  if (isLinkedProductPermission(permissionId)) {
    // 등록 또는 수정 중 하나를 선택하면 둘 다 선택
    if (checked) {
      currentPermissions.add('products:write');
      currentPermissions.add('products:write-edit');
    } else {
      // 둘 중 하나를 해제하면 둘 다 해제
      currentPermissions.delete('products:write');
      currentPermissions.delete('products:write-edit');
    }
  } else {
    // 다른 권한은 일반적으로 처리
    if (checked) {
      currentPermissions.add(permissionId);
    } else {
      currentPermissions.delete(permissionId);
    }
  }
}
